import { secureFetch } from './auth'
import { HttpError, failureMessage } from './orders-api'

// R199 (b) — the HOP /kitchen screen's API. Online only, the dispatch-api.ts shape: the
// offline queue turns any non-400 4xx (a 409 included) into a terminal hop:action-failed, so
// a kitchen write made offline fails visibly instead. The server makes replays safe.

// R285: COMPLETED rides only on the kitchen TV's read (doneWithinHours); /kitchen never gets it.
export type KitchenTaskStatus = 'PENDING' | 'IN_PROGRESS' | 'BLOCKED' | 'COMPLETED'
export type KitchenOpenStatus = Exclude<KitchenTaskStatus, 'COMPLETED'>

export const KITCHEN_BLOCK_REASONS = ['MISSING_INGREDIENT', 'ALLERGY_SAFETY', 'EQUIPMENT', 'OTHER'] as const

export type KitchenBlockReason = (typeof KITCHEN_BLOCK_REASONS)[number]

export interface KitchenTask {
  taskId: string
  orderId: string
  orderCode: string
  placedAt: string
  deliveryDate: string | null // YYYY-MM-DDT00:00:00.000Z
  deliverySlot: string | null
  firstName: string | null
  mealName: string
  nameEn: string
  nameAr: string | null
  quantity: number
  modifications: unknown // a JSON column: string, array, object or null
  orderNotes: string | null
  status: KitchenTaskStatus
  blockReason: string | null
  blockNote: string | null
  startedAt: string | null
  blockedAt: string | null
  createdAt: string
  completedAt: string | null // the task's updatedAt when COMPLETED, else null
  imageUrl: string | null
}

export async function fetchKitchenTasks(opts?: { doneWithinHours?: number }): Promise<KitchenTask[]> {
  const hours = opts?.doneWithinHours
  const res = await secureFetch(
    hours === undefined ? '/api/kitchen/tasks' : `/api/kitchen/tasks?doneWithinHours=${hours}`,
  )
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<KitchenTask[]>
}

async function postTask(id: string, action: string, body?: unknown): Promise<void> {
  const res = await secureFetch(`/api/kitchen/tasks/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  })
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
}

export function startTask(id: string): Promise<void> {
  return postTask(id, 'start')
}

export function blockTask(id: string, reason: KitchenBlockReason, note?: string): Promise<void> {
  return postTask(id, 'block', { reason, note })
}

export function resumeTask(id: string): Promise<void> {
  return postTask(id, 'resume')
}

export function completeTask(id: string): Promise<void> {
  return postTask(id, 'complete')
}

// Each helper enumerates its roles; none is "not X". `viewer` reads in ops-web, not HOP.
const KITCHEN_SEE_ROLES = ['kitchen', 'hop', 'admin', 'owner', 'architect']
// R285: the wall TV signs in as a read-only `viewer` (kitchen-tv); it sees /kitchen/tv only.
const KITCHEN_TV_SEE_ROLES = ['kitchen', 'hop', 'admin', 'owner', 'architect', 'viewer']
const KITCHEN_ACT_ROLES = ['kitchen', 'admin', 'owner', 'architect']
const KITCHEN_RESUME_ROLES = ['kitchen', 'hop', 'admin', 'owner', 'architect']

export function canSeeKitchen(roles: string[]): boolean {
  return roles.some((r) => KITCHEN_SEE_ROLES.includes(r))
}

export function canSeeKitchenTv(roles: string[]): boolean {
  return roles.some((r) => KITCHEN_TV_SEE_ROLES.includes(r))
}

export function canActInKitchen(roles: string[]): boolean {
  return roles.some((r) => KITCHEN_ACT_ROLES.includes(r))
}

export function canResumeInKitchen(roles: string[]): boolean {
  return roles.some((r) => KITCHEN_RESUME_ROLES.includes(r))
}
